"use client"

import {useEffect, useRef, useState} from "react";
import {Bold} from "@/components/DownloaderApp/Bold";
import {useModal} from "@/components/DownloaderApp/Modal";
import {SafariDownloadDemoPopup} from "@/components/DownloaderApp/SafariDownloadDemoPopup";
import {
    isAndroidUserAgent,
    isAppleMobileUserAgent,
    isMobileSafariUserAgent
} from "@/components/DownloaderApp/browser-detection";
import type {DownloadSaveInfo} from "@/components/DownloaderApp/types";

interface DownloadSavedGuideProps {
    emoji: string;
    saveInfo: DownloadSaveInfo;
    userAgent: string;
}

function FileName({
    children,
    copied,
    onCopy,
}: {
    children: string;
    copied: boolean;
    onCopy?: () => void;
}) {
    const fileNamePrefix = children.slice(0, -1);
    const fileNameSuffix = children.slice(-1);

    return (
        <span>
            <Bold className="break-all">{fileNamePrefix}</Bold>
            <span className="whitespace-nowrap">
                <Bold>{fileNameSuffix}</Bold>
                {onCopy && (
                    <button
                        type="button"
                        className="ml-1 inline-flex cursor-pointer align-middle rounded border border-neutral-300 p-1 text-neutral-600 transition-colors hover:bg-neutral-200 hover:text-neutral-950 dark:border-neutral-600 dark:text-neutral-300 dark:hover:bg-neutral-700 dark:hover:text-white"
                        onClick={onCopy}
                        aria-label={copied ? "File name copied" : "Copy file name"}
                        title={copied ? "File name copied" : "Copy file name"}
                    >
                        {copied ? (
                            <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="m5 12 4 4L19 6"/>
                            </svg>
                        ) : (
                            <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="9" y="9" width="11" height="11" rx="2"/>
                                <path d="M15 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h3"/>
                            </svg>
                        )}
                    </button>
                )}
            </span>
        </span>
    );
}

export function DownloadSavedGuide({emoji, saveInfo, userAgent}: DownloadSavedGuideProps) {
    const {show} = useModal();
    const copyResetTimeoutRef = useRef<number | null>(null);
    const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "error">("idle");
    const showSafariGuide = saveInfo.location === "downloads" && isMobileSafariUserAgent(userAgent);

    useEffect(() => {
        return () => {
            if (copyResetTimeoutRef.current !== null) {
                window.clearTimeout(copyResetTimeoutRef.current);
            }
        };
    }, []);

    async function copyFileName() {
        if (!saveInfo.fileName) return;

        try {
            await navigator.clipboard.writeText(saveInfo.fileName);
            setCopyStatus("copied");
            copyResetTimeoutRef.current = window.setTimeout(() => {
                setCopyStatus("idle");
                copyResetTimeoutRef.current = null;
            }, 1200);
        } catch (err) {
            console.error("Failed to copy download filename", err);
            setCopyStatus("error");
        }
    }

    const fileName = (
        <FileName
            copied={copyStatus === "copied"}
            onCopy={saveInfo.fileName ? copyFileName : undefined}
        >
            {saveInfo.fileName || "your video"}
        </FileName>
    );

    return (
        <div
            className="my-8 rounded-2xl border border-neutral-200 px-5 py-5 text-neutral-900 shadow-lg shadow-black/5 dark:border-neutral-700 dark:text-neutral-100"
            role="note"
        >
            <p className="text-center text-lg">
                <span className="font-bold">
                    <Bold>{saveInfo.location === "browser" ? "Download ready!" : "Video saved successfully!"}</Bold>
                </span>{" "}{emoji}
            </p>

            {showSafariGuide ? (
                <ol className="mt-4 flex flex-col gap-4 text-sm font-medium sm:text-base">
                    <li className="rounded-xl bg-white/70 p-3 dark:bg-black/20">
                        <div className="flex items-center gap-3">
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#51fc1a] font-bold text-neutral-950">1</span>
                            <span>Tap the <Bold>download icon</Bold> in <Bold>Safari&apos;s address bar</Bold>.</span>
                        </div>
                        <img
                            className="mt-3 h-auto w-full rounded-lg"
                            src="/safari-file-tutorial/mobile-help-toolbar.jpg"
                            width="1060"
                            height="220"
                            alt="Safari bottom address bar with the download icon"
                        />
                    </li>
                    <li className="rounded-xl bg-white/70 p-3 dark:bg-black/20">
                        <div className="flex items-center gap-3">
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#51fc1a] font-bold text-neutral-950">2</span>
                            <span>
                                Tap <Bold>Downloads</Bold>, then select{" "}
                                {fileName}.
                            </span>
                        </div>
                        <img
                            className="mx-auto mt-3 h-auto w-full max-w-[300px] rounded-lg"
                            src="/safari-file-tutorial/mobile-help-menu.jpg"
                            width="1120"
                            height="1500"
                            alt="Safari menu with the Downloads option"
                        />
                    </li>
                    <li className="rounded-xl bg-white/70 p-3 dark:bg-black/20">
                        <div className="flex items-center gap-3">
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#51fc1a] font-bold text-neutral-950">3</span>
                            <span>
                                Need more help?{" "}
                                <button
                                    type="button"
                                    className="font-bold text-[#51fc1a] underline underline-offset-4"
                                    onClick={() => show({
                                        component: SafariDownloadDemoPopup,
                                        data: {size: "md"},
                                    })}
                                >
                                    Watch the short demo
                                </button>
                                .
                            </span>
                        </div>
                    </li>
                </ol>
            ) : (
                <p className="mt-4 rounded-xl bg-white/70 p-4 text-center text-sm font-medium dark:bg-black/20 sm:text-base">
                    {saveInfo.location === "chosen-location" && (
                        <>Find file {fileName} in the <Bold>folder you selected</Bold>.</>
                    )}
                    {saveInfo.location === "downloads" && isAndroidUserAgent(userAgent) && (
                        <>Open the <Bold>Files</Bold> app, then <Bold>Downloads</Bold>, to find {fileName}.</>
                    )}
                    {saveInfo.location === "downloads" && isAppleMobileUserAgent(userAgent) && (
                        <>Open the <Bold>Files</Bold> app, then <Bold>Downloads</Bold>, to find {fileName}.</>
                    )}
                    {saveInfo.location === "downloads" && !isAndroidUserAgent(userAgent) && !isAppleMobileUserAgent(userAgent) && (
                        <>Find {fileName} in your browser&apos;s <Bold>Downloads</Bold> folder.</>
                    )}
                    {saveInfo.location === "browser" && (
                        <>
                            Check your browser&apos;s download directory.
                            <br/>
                            If the video opened in a new tab, use the download button there.
                        </>
                    )}
                </p>
            )}
            {saveInfo.location === "downloads" && (
                <div className="mt-4 rounded-xl bg-white/70 p-4 text-sm dark:bg-black/20 sm:text-base">
                    <p><Bold>Want to save somewhere else?</Bold></p>
                    {isAppleMobileUserAgent(userAgent) ? (
                        <p className="mt-2">
                            In the <Bold>Files</Bold> app, touch and hold the downloaded file, then choose <Bold>Move</Bold>.
                            Select another folder or a connected external drive listed under <Bold>Locations</Bold>.
                            For future Safari downloads, open <Bold>Settings &gt; Apps &gt; Safari &gt; Downloads</Bold>{" "}
                            (or <Bold>Settings &gt; Safari &gt; Downloads</Bold> on older iOS versions) and choose <Bold>Other...</Bold>
                        </p>
                    ) : isAndroidUserAgent(userAgent) ? (
                        <p className="mt-2">
                            Check your browser&apos;s <Bold>Settings &gt; Downloads</Bold> for a location or{" "}
                            <Bold>Ask where to save files</Bold> option. You can also move the completed download
                            to another folder using the <Bold>Files</Bold> app.
                        </p>
                    ) : (
                        <>
                            <p className="mt-2">
                                Your browser controls the download destination. For future downloads, choose another folder
                                or a connected external drive in its settings:
                            </p>
                            <ul className="mt-2 list-disc space-y-2 pl-5">
                                <li>
                                    <Bold>Safari on Mac:</Bold>{" "}Safari &gt; Settings &gt; General &gt; File download location.
                                    Choose <Bold>Other...</Bold> for a folder or <Bold>Ask for each download</Bold> to pick every time.
                                </li>
                                <li>
                                    <Bold>Firefox:</Bold>{" "}Settings &gt; General &gt; Files and Applications &gt; Downloads.
                                    Choose a folder under <Bold>Save files to</Bold> or enable{" "}
                                    <Bold>Always ask you where to save files</Bold>.
                                </li>
                                <li>
                                    <Bold>Chrome, Edge, or Brave:</Bold>{" "}Settings &gt; Downloads.
                                    Change the <Bold>Location</Bold> or enable the option to ask where to save each file.
                                </li>
                            </ul>
                            <p className="mt-2">Changing these settings does not move files you have already downloaded.</p>
                        </>
                    )}
                </div>
            )}
            {copyStatus === "error" && (
                <p className="mt-2 text-center text-sm text-red-600 dark:text-red-400" role="alert">
                    Could not copy the file name.
                </p>
            )}
        </div>
    );
}
